import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type { ListingInput, TrailerType } from '@shared/api-types.js';

const MINE = ['listings', 'mine'] as const;
const PUBLIC = (filters: { type?: TrailerType }) =>
  ['listings', 'public', filters] as const;
const ONE = (idOrSlug: string) => ['listings', 'one', idOrSlug] as const;

export function useMyListings() {
  return useQuery({
    queryKey: MINE,
    queryFn: () => api.listings.mine(),
  });
}

export function useListings(filters: { type?: TrailerType } = {}) {
  return useQuery({
    queryKey: PUBLIC(filters),
    queryFn: () => api.listings.list(filters),
  });
}

export function useListing(idOrSlug: string | undefined) {
  return useQuery({
    queryKey: idOrSlug ? ONE(idOrSlug) : ['listings', 'one', 'none'],
    queryFn: () => api.listings.get(idOrSlug!),
    enabled: Boolean(idOrSlug),
  });
}

export function useCreateListing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ListingInput) => api.listings.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['listings'] });
    },
  });
}

export function useUpdateListing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<ListingInput> }) =>
      api.listings.update(id, input),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['listings'] });
      qc.setQueryData(ONE(data.listing.id), data);
      qc.setQueryData(ONE(data.listing.slug), data);
    },
  });
}

export function useDeleteListing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.listings.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['listings'] });
    },
  });
}
